import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { FadeUp, Overline, MaskedLines } from "@/components/Reveal";
import { EMAIL, WHATSAPP_URL, SERVICE_OPTIONS, VOLUME_OPTIONS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  company: "",
  email: "",
  phone: "",
  website: "",
  volume: "",
  file_link: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [services, setServices] = useState([]);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleService = (s) =>
    setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, services: services.join(", ") });
      toast.success("Enquiry received. Our studio will respond shortly.");
      setForm(EMPTY);
      setServices([]);
    } catch (err) {
      toast.error("Something went wrong. Please email us directly at info@vcontractz.com.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-28 md:py-40">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <FadeUp>
            <Overline>Contact</Overline>
          </FadeUp>
          <h2 className="mt-8 font-display font-light text-4xl md:text-5xl lg:text-6xl leading-[1.0] tracking-tight">
            <MaskedLines lines={["LET\u2019S CREATE", "SOMETHING", "EXCEPTIONAL."]} />
          </h2>
          <FadeUp delay={0.2} className="mt-8 max-w-md">
            <p className="text-sm md:text-base text-ivory/70 font-light leading-relaxed">
              Send a test image, a full project, or simply tell us how you work.
              We respond with a tailored workflow and custom pricing.
            </p>
          </FadeUp>

          <FadeUp delay={0.3} className="mt-12 space-y-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-muted">Email</p>
              <a data-testid="contact-email-link" href={`mailto:${EMAIL}`} className="mt-1 inline-block font-display text-xl md:text-2xl text-ivory hover:text-gold transition-colors duration-300">
                {EMAIL}
              </a>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-muted">WhatsApp</p>
              <a data-testid="contact-whatsapp-link" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block font-display text-xl md:text-2xl text-ivory hover:text-gold transition-colors duration-300">
                +91 95357 53697
              </a>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-muted">Studio</p>
              <p className="mt-1 text-sm text-ivory/80">Vcontractz Solutions India Pvt. Ltd.</p>
              <p className="text-[11px] tracking-[0.15em] text-muted mt-1">Serving clients worldwide</p>
            </div>
          </FadeUp>
        </div>

        <FadeUp delay={0.15}>
          <form data-testid="contact-form" onSubmit={submit} className="border hairline p-8 md:p-10 bg-charcoal/40 space-y-7">
            <div className="grid sm:grid-cols-2 gap-7">
              <div>
                <label htmlFor="cf-name" className="block text-[10px] uppercase tracking-[0.3em] text-muted mb-1">Name *</label>
                <input id="cf-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className="input-line" placeholder="Your name" />
              </div>
              <div>
                <label htmlFor="cf-company" className="block text-[10px] uppercase tracking-[0.3em] text-muted mb-1">Company</label>
                <input id="cf-company" data-testid="contact-company-input" value={form.company} onChange={set("company")} className="input-line" placeholder="Studio / agency" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-7">
              <div>
                <label htmlFor="cf-email" className="block text-[10px] uppercase tracking-[0.3em] text-muted mb-1">Email *</label>
                <input id="cf-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} className="input-line" placeholder="you@studio.com" />
              </div>
              <div>
                <label htmlFor="cf-phone" className="block text-[10px] uppercase tracking-[0.3em] text-muted mb-1">WhatsApp / Phone</label>
                <input id="cf-phone" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} className="input-line" placeholder="+1 ..." />
              </div>
            </div>
            <div>
              <label htmlFor="cf-website" className="block text-[10px] uppercase tracking-[0.3em] text-muted mb-1">Website</label>
              <input id="cf-website" data-testid="contact-website-input" value={form.website} onChange={set("website")} className="input-line" placeholder="https://" />
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-muted mb-3">Services Required</p>
              <div className="flex flex-wrap gap-2" data-testid="contact-services-chips">
                {SERVICE_OPTIONS.map((s) => (
                  <button
                    type="button"
                    key={s}
                    data-testid={`contact-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => toggleService(s)}
                    className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] border transition-colors duration-300 ${
                      services.includes(s) ? "border-gold text-gold" : "hairline text-ivory/60 hover:text-ivory"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="cf-volume" className="block text-[10px] uppercase tracking-[0.3em] text-muted mb-1">Approximate Monthly Volume</label>
              <select id="cf-volume" data-testid="contact-volume-select" value={form.volume} onChange={set("volume")} className="input-line bg-ink">
                <option value="">Select volume</option>
                {VOLUME_OPTIONS.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="cf-filelink" className="block text-[10px] uppercase tracking-[0.3em] text-muted mb-1">Sample / Project Files</label>
              <input id="cf-filelink" data-testid="contact-filelink-input" value={form.file_link} onChange={set("file_link")} className="input-line" placeholder="Dropbox / Drive / WeTransfer link" />
              <p className="mt-2 text-[10px] text-muted/70 tracking-wide">Share a link to your files — we handle the rest.</p>
            </div>

            <div>
              <label htmlFor="cf-message" className="block text-[10px] uppercase tracking-[0.3em] text-muted mb-1">Message</label>
              <textarea id="cf-message" data-testid="contact-message-input" rows={4} value={form.message} onChange={set("message")} className="input-line resize-none" placeholder="Tell us about your workflow, style and deadlines" />
            </div>

            <button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={sending}
              className="btn-primary w-full justify-center disabled:opacity-60"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <ArrowUpRight size={14} />}
              {sending ? "Sending..." : "Send Project Enquiry"}
            </button>
          </form>
        </FadeUp>
      </div>
    </section>
  );
}
